// Hand flight: the player's own six-degree-of-freedom model for cruise.
//
// Pure. No three.js, no DOM, no clock: a state, a step, and the position delta
// the step produced. The frame is the caller's business - `src/main.ts` runs it
// in heliocentric ecliptic J2000 metres, the frame `src/sim/position.ts` holds
// the authoritative doubles in, so a re-seed of the combat bubble's own basis
// leaves the coast pointing where it was pointing. `src/sim/transfer.ts` plans
// its burns in that same frame in KILOMETRES, so one of its burns flown through
// here scales by 1e3, and a GM by 1e9.
//
// Nothing here slows the ship down. `config.drag` exists for the broken-on-
// purpose `?drag=1` variant the `--flight --inject drag` control opens, and is
// 0 on every path a player reaches.
//
// Gravity arrives the same way the frame does: as something the caller hands
// in. `stepFlight` takes the ship's own place and a list of pulling bodies,
// each with its centre in that frame and its GM; it never looks a body up and
// never decides which bodies pull. `src/main.ts` picks the set per tick off
// the registry's own masses.

import type { FlightGround, GroundHit } from './ground.ts';
import { centreOf, createGroundHit, findGroundContact, groundSlot } from './ground.ts';

/** A vector; `THREE.Vector3` satisfies it. */
export interface FlightVec {
  x: number;
  y: number;
  z: number;
}

/** A unit quaternion; `THREE.Quaternion` satisfies it. */
export interface FlightQuat {
  x: number;
  y: number;
  z: number;
  w: number;
}

/**
 * Held rotation rates, each normalised to [-1, 1] and taken about the ship's
 * OWN axes: pitch about body +X (positive is nose up), yaw about body +Y
 * (positive is nose left), roll about body +Z (positive is counter-clockwise
 * seen from behind). The nose is body -Z.
 */
export interface FlightAxes {
  pitch: number;
  yaw: number;
  roll: number;
}

/** One body's pull: where its centre is, in the caller's frame, and its GM. */
export interface FlightPull {
  /** Standard gravitational parameter, m3/s2. 0 is a slot nothing fills. */
  gm: number;
  /** The body's centre, in the caller's frame. */
  at: FlightVec;
}

/** Where the ship is, and what is pulling on it there. Read, never written. */
export interface FlightField {
  /** The ship's own place at the START of the step, in the caller's frame. */
  at: FlightVec;
  pulls: readonly FlightPull[];
  /**
   * The acceleration of the frame the state is expressed in, when that frame
   * is itself falling - the body pulling hardest, taken at its OWN place.
   * Subtracted from the field every substep, which leaves that body's own term
   * exact (`c - (c + r)` is `-r` whatever instant `c` was sampled at) and every
   * other body's as the tidal difference between its pull on the ship and its
   * pull on the frame. That is what lets the ephemeris be sampled once a frame
   * while the substeps run at 2 s: at the top warp level the frame body travels
   * ten orbital radii between two samples. Null or absent is an inertial frame,
   * which is what this read before the frame became parent-relative.
   */
  frameAccel?: FlightVec | null;
  /**
   * The surfaces this step may meet, one entry per `pulls` slot. Null or
   * absent is the pre-fix field, in which nothing stops the ship at a body's
   * radius and a run at a centre falls through it and is flung back out.
   */
  ground?: FlightGround | null;
}

export interface FlightState {
  /** The ship's attitude in the caller's frame. */
  orientation: FlightQuat;
  /**
   * Where the player has told the assist to put the ship's motion: a unit
   * vector in the caller's frame, or null while nothing has commanded one.
   *
   * The aim and the nose are separate things. The boot seed points the bow at
   * the body the ship orbits for framing, which on a circular orbit is 90
   * degrees off the direction of travel by construction, so the bow can go on
   * framing the body at no cost while the aim stays where it was put.
   *
   * Null is not "aim at nothing": an uncommanded aim IS the ship's own
   * velocity, which is zero gap and zero spend, and `applyAssist` returns on
   * it rather than closing a gap of floating-point dust.
   *
   * A commanded one rides the velocity's own turning under a pull rather than
   * standing still in the frame (`carryAim` below): an orbit turns the velocity
   * out from under a fixed direction at `g / speed` radians a second, and an
   * assist billed for that buys back gravity at `g` metres a second per
   * simulated second until the orbit is gone.
   */
  assistAim: FlightVec | null;
  /** Metres per second in the caller's frame. */
  velocity: FlightVec;
  /** Metres per second of thrust spent this session. Only grows. */
  deltaVSpent: number;
  /**
   * Armed by the caller on the assist's OFF->ON edge (`src/main.ts`'s
   * `onAssistToggle`). Only `config.assistHeldOnly` reads it: under the
   * ordinary rule a gap closes whether or not an axis is held, so an OFF->ON
   * edge needs nothing armed. `applyAssist` clears it once the gap is gone;
   * only the caller ever sets it true.
   */
  assistCatchUp: boolean;
  /**
   * The body the ship is resting on, or null while it is flying. Set by
   * `stepFlight` when a substep's segment meets a surface and cleared when a
   * burn with a component out of the ground lifts the ship off it.
   */
  grounded: string | null;
  /**
   * Where on that body it is resting: the ship's own offset from the body's
   * centre, in the caller's frame. Held rather than recomputed, so the ship
   * rides the body's own travel while it sits there, and turned by the body's
   * spin every substep it rests through (`FlightGround.spinBy`), so it stays
   * on the ground the contact met.
   */
  groundOffset: FlightVec;
  /**
   * Speed of the hit that grounded it, m/s relative to that body. 0 while
   * flying. Nothing in the model reads it; the HUD notice does, and a damage
   * rule that does not exist yet would.
   */
  contactSpeedMps: number;
  /**
   * The direction the assist's lateral thrusters acted along over the last
   * step, in the caller's frame, each substep's own turn plane weighted by the
   * arc it turned. Zero is no lateral thrust, and the length carries the total
   * arc rather than a unit.
   *
   * Written here because it cannot be recovered afterwards: the assist turns
   * the velocity ONTO the aim, so a step that closed the gap leaves the two
   * parallel and the plane it turned in gone. Read by `thrustLateral` in
   * src/main.ts to point the drawn plumes, and by nothing in the physics.
   */
  assistPush: FlightVec;
  /**
   * Did the per-call cap stop the assist short of the gap on any substep of
   * the last step? The panel says so rather than letting the ship burn quietly
   * while the velocity never catches the nose: at cruise speed a full yaw
   * closes a percent or two of itself and the spend goes on for as long as the
   * gap is open. Written here for the same reason `assistPush` is - once the
   * step is over, `gap` and the cap it was measured against are both gone.
   */
  assistCapped: boolean;
}

export interface FlightConfig {
  /** Radians per second at a full axis deflection. */
  turnRate: number;
  /** Metres per second squared at full throttle. */
  accel: number;
  /**
   * Per-second velocity decay. 0 everywhere a player flies; non-zero only for
   * the `?drag=1` variant, which is what the coast reading is meant to catch.
   */
  drag: number;
  /**
   * Longest slice of sim seconds one integration substep may take once
   * something is pulling. A frame asking for more is split, so a warped clock
   * or a slow frame buys MORE substeps rather than a longer one and the
   * integrator's own error stays where this number puts it.
   */
  maxSubstepS: number;
  /** Most substeps one call may take. Past it the substeps lengthen again. */
  maxSubsteps: number;
  /**
   * The assist, broken on purpose: snap the velocity straight onto the nose
   * and charge nothing for it, which is neither a rate the thrust can deliver
   * nor a cost. `?assistfree=1` sets it and nothing a player reaches does.
   */
  assistFree: boolean;
  /**
   * The assist gated on a held axis again: only a held pitch or yaw (or an
   * armed `assistCatchUp`) corrects anything, so an aim dragged over and let
   * go freezes where the drag left it. `?assistheldonly=1` sets it and
   * nothing a player reaches does.
   */
  assistHeldOnly: boolean;
  /**
   * The aim frozen in the flight frame again instead of riding the rotation a
   * pull puts on the velocity, so the assist buys back gravity's own turning
   * and a parked orbit sinks under it. `?assistfrozenaim=1` sets it and
   * nothing a player reaches does.
   */
  assistFrozenAim: boolean;
  /**
   * `ASSIST_GAP_MIN_RAD` back to zero, so a closed gap is still a gap and the
   * assist turns the velocity by float residue every substep in a direction
   * that size cannot carry. `?assistnofloor=1` sets it and nothing a player
   * reaches does.
   */
  assistNoFloor: boolean;
  /**
   * Liftoff drops the tangential velocity a resting offset carried under the
   * body's own spin instead of adding it back, which is the deficit the seat
   * left when it started turning with the ground. `?liftoffinertial=1` sets
   * it and nothing a player reaches does. Optional; undefined is false.
   */
  dropLiftoffSpin?: boolean;
  /**
   * A resting ship's orientation stays inertial instead of turning with the
   * ground under it, which is the hull yawing against still terrain the seat
   * fix left behind. `?headinginertial=1` sets it and nothing a player
   * reaches does. Optional; undefined is false.
   */
  inertialHeading?: boolean;
}

/**
 * Radians of gap under which the assist has nothing to correct and stops.
 *
 * A deadband, and the size of it is set by the DIRECTION rather than by the
 * angle: the plane the assist turns in is the aim with its component along the
 * velocity removed, so the two O(1) terms it is a difference of leave it an
 * absolute error of about 2e-16 whatever the gap, and its direction is only
 * meaningful while the gap is well above that. At this floor the plane is good
 * to a hundredth of a degree; two orders lower it is good to a degree, and at
 * the residue two rotations actually leave - about 5e-16 rad - it reverses
 * between substeps.
 *
 * It swallows no commanded turn. One frame of the turn rate opens about
 * 1.3e-2 rad and one 2 s substep of gravity's own turning at a moon's orbit
 * about 5e-4; both are many orders above this.
 */
export const ASSIST_GAP_MIN_RAD = 1e-12;

const ZERO_AXES: FlightAxes = { pitch: 0, yaw: 0, roll: 0 };

export function noAxes(): FlightAxes {
  return { ...ZERO_AXES };
}

export function createFlightState(): FlightState {
  return {
    orientation: { x: 0, y: 0, z: 0, w: 1 },
    assistAim: null,
    velocity: { x: 0, y: 0, z: 0 },
    deltaVSpent: 0,
    assistCatchUp: false,
    grounded: null,
    groundOffset: { x: 0, y: 0, z: 0 },
    contactSpeedMps: 0,
    assistPush: { x: 0, y: 0, z: 0 },
    assistCapped: false,
  };
}

/**
 * The velocity the assist acts on, and the one an aim is seeded from. The
 * handle itself, since the assist rotates it in place.
 *
 * ONE accessor on purpose. The state is stepped in whatever frame the caller
 * holds, and a parent-relative ship state would change which velocity the
 * assist should be reading; this is the single line that reconciles the gap,
 * the cost and the aim seed together.
 */
export function assistVelocity(state: FlightState): FlightVec {
  return state.velocity;
}

/** Metres per second, in whatever frame the caller stepped the state in. */
export function flightSpeed(state: FlightState): number {
  const { x, y, z } = state.velocity;
  return Math.hypot(x, y, z);
}

/** The ship's own forward direction (body -Z), as a unit vector. */
export function flightNose<T extends FlightVec>(state: FlightState, out: T): T {
  const { x, y, z, w } = state.orientation;
  // q * (0,0,-1) * q^-1, written out.
  out.x = -2 * (x * z + w * y);
  out.y = -2 * (y * z - w * x);
  out.z = -(1 - 2 * (x * x + y * y));
  return out;
}

/** Command the aim along the ship's own nose: a turn is the player naming one. */
export function aimAlongNose(state: FlightState): void {
  if (state.assistAim === null) state.assistAim = { x: 0, y: 0, z: 0 };
  flightNose(state, state.assistAim);
}

/**
 * Command the aim along the ship's own velocity, which costs nothing to hold
 * at the instant it is given. A park that puts the nose prograde is commanding
 * exactly this.
 */
export function aimAlongVelocity(state: FlightState): void {
  const v = assistVelocity(state);
  const l = Math.hypot(v.x, v.y, v.z);
  if (!(l > 0)) return;
  if (state.assistAim === null) state.assistAim = { x: 0, y: 0, z: 0 };
  state.assistAim.x = v.x / l;
  state.assistAim.y = v.y / l;
  state.assistAim.z = v.z / l;
}

/** Copy one state's commanded aim onto another, allocating at most once. */
export function copyAssistAim(from: FlightState, to: FlightState): void {
  const aim = from.assistAim;
  if (aim === null) { to.assistAim = null; return; }
  if (to.assistAim === null) to.assistAim = { x: 0, y: 0, z: 0 };
  to.assistAim.x = aim.x;
  to.assistAim.y = aim.y;
  to.assistAim.z = aim.z;
}

/** A body axis in the caller's frame. `axis` is a unit vector in body metres. */
export function flightRotate<T extends FlightVec>(state: FlightState, axis: FlightVec, out: T): T {
  const { x, y, z, w } = state.orientation;
  const { x: ax, y: ay, z: az } = axis;
  // t = 2 * (q.xyz x axis); out = axis + w * t + q.xyz x t
  const tx = 2 * (y * az - z * ay);
  const ty = 2 * (z * ax - x * az);
  const tz = 2 * (x * ay - y * ax);
  out.x = ax + w * tx + (y * tz - z * ty);
  out.y = ay + w * ty + (z * tx - x * tz);
  out.z = az + w * tz + (x * ty - y * tx);
  return out;
}

/** The inverse of `flightRotate`: a vector in the caller's frame, in body metres. */
export function flightUnrotate<T extends FlightVec>(state: FlightState, v: FlightVec, out: T): T {
  const { x, y, z, w } = state.orientation;
  const { x: vx, y: vy, z: vz } = v;
  // The conjugate quaternion, so the cross products take the negated axis.
  const tx = -2 * (y * vz - z * vy);
  const ty = -2 * (z * vx - x * vz);
  const tz = -2 * (x * vy - y * vx);
  out.x = vx + w * tx - (y * tz - z * ty);
  out.y = vy + w * ty - (z * tx - x * tz);
  out.z = vz + w * tz - (x * ty - y * tx);
  return out;
}

/** Post-multiply by an axis-angle rotation taken in the BODY frame. */
function rotateBody(q: FlightQuat, nx: number, ny: number, nz: number, angle: number): void {
  const s = Math.sin(angle * 0.5);
  const dx = nx * s;
  const dy = ny * s;
  const dz = nz * s;
  const dw = Math.cos(angle * 0.5);
  const { x, y, z, w } = q;
  q.x = w * dx + x * dw + y * dz - z * dy;
  q.y = w * dy - x * dz + y * dw + z * dx;
  q.z = w * dz + x * dy - y * dx + z * dw;
  q.w = w * dw - x * dx - y * dy - z * dz;
  const l = Math.hypot(q.x, q.y, q.z, q.w);
  if (l > 0) {
    q.x /= l;
    q.y /= l;
    q.z /= l;
    q.w /= l;
  }
}

/**
 * Pre-multiply by an axis-angle rotation taken in the CALLER's frame: what a
 * resting ship's orientation needs to turn with the ground by the same axis
 * and angle `FlightGround.spinBy` turns the offset by, since that spin is
 * about the body's own pole in the caller's axes rather than about the ship's.
 */
function rotateWorld(q: FlightQuat, nx: number, ny: number, nz: number, angle: number): void {
  const s = Math.sin(angle * 0.5);
  const dx = nx * s;
  const dy = ny * s;
  const dz = nz * s;
  const dw = Math.cos(angle * 0.5);
  const { x, y, z, w } = q;
  q.x = dw * x + dx * w + dy * z - dz * y;
  q.y = dw * y - dx * z + dy * w + dz * x;
  q.z = dw * z + dx * y - dy * x + dz * w;
  q.w = dw * w - dx * x - dy * y - dz * z;
  const l = Math.hypot(q.x, q.y, q.z, q.w);
  if (l > 0) {
    q.x /= l;
    q.y /= l;
    q.z /= l;
    q.w /= l;
  }
}

/**
 * The gravitational acceleration at `at`, summed over `pulls`. A pull with no
 * GM, or one the point sits exactly on, contributes nothing.
 */
export function flightGravity<T extends FlightVec>(
  at: FlightVec, pulls: readonly FlightPull[], out: T,
): T {
  out.x = 0;
  out.y = 0;
  out.z = 0;
  for (let i = 0; i < pulls.length; i++) {
    const p = pulls[i]!;
    if (!(p.gm > 0)) continue;
    const dx = p.at.x - at.x;
    const dy = p.at.y - at.y;
    const dz = p.at.z - at.z;
    const r2 = dx * dx + dy * dy + dz * dz;
    if (!(r2 > 0)) continue;
    const k = p.gm / (r2 * Math.sqrt(r2));
    out.x += dx * k;
    out.y += dy * k;
    out.z += dz * k;
  }
  return out;
}

/** Specific potential energy at `at`: the sum of -GM/r, joules per kilogram. */
export function flightPotential(at: FlightVec, pulls: readonly FlightPull[]): number {
  let u = 0;
  for (let i = 0; i < pulls.length; i++) {
    const p = pulls[i]!;
    if (!(p.gm > 0)) continue;
    const r = Math.hypot(p.at.x - at.x, p.at.y - at.y, p.at.z - at.z);
    if (!(r > 0)) continue;
    u -= p.gm / r;
  }
  return u;
}

/**
 * How many substeps `dt` takes under this config. Pure, so a caller can report
 * it. A live assist splits the step the way a pull does, since its own cap is
 * per substep and nothing else would bound one under warp.
 */
export function flightSubsteps(
  config: FlightConfig, dt: number, pulls: number, assist = false,
): number {
  if (!(dt > 0)) return 0;
  if ((pulls <= 0 && !assist) || !(config.maxSubstepS > 0)) return 1;
  const n = Math.ceil(dt / config.maxSubstepS);
  if (!(n >= 1)) return 1;
  return n > config.maxSubsteps ? Math.max(1, config.maxSubsteps) : n;
}

/** Drift-kick-drift slips an orbit's phase as the step squared: 800 steps a turn
 *  slip a 400 km Earth orbit 0.11 deg a day, where the 46 a 120 s step gives slip 34. */
const ORBIT_SUBSTEPS_PER_REV = 800;

/** The longest substep, capped at `capS`, that keeps a ship `radiusM` from a body of `gm` on its orbit's phase. */
export function orbitSubstepS(capS: number, gm: number, radiusM: number): number {
  if (!(gm > 0) || !(radiusM > 0)) return capS;
  const periodS = 2 * Math.PI * Math.sqrt((radiusM * radiusM * radiusM) / gm);
  return Math.min(capS, periodS / ORBIT_SUBSTEPS_PER_REV);
}

/**
 * The flight assist: while it is on, lateral thrusters bring the ship's own
 * motion round onto the aim the player commanded.
 *
 * The velocity is ROTATED toward the aim rather than pushed at it, so the
 * speed is untouched and only the direction moves, and the arc it turned is
 * charged to `deltaVSpent` at the same `config.accel` the main engine spends.
 * The turn per call is capped at `accel * assistDt / speed`, which is the
 * angle that lateral acceleration covers at this speed, and at the gap itself,
 * so the velocity settles on the nose and then tracks it instead of swinging
 * past. Above a few hundred metres a second the cap binds and the ship drifts
 * wide of its nose, which is what a thruster of that size actually does.
 *
 * `state.assistAim` is the one precondition, and it is a DIRECTION rather than
 * the pose the ship happens to be in: until the player has commanded one there
 * is no gap, so switching the assist on with no turn behind it closes nothing.
 * A pitch or yaw commands one in `stepFlight` below and a park commands one
 * along the velocity; nothing else does. It is not a direction in the frame
 * either: `carryAim` turns it with the velocity under a pull, so the gap this
 * reads is the one the player opened and never gravity's own turning.
 *
 * A held axis is not a precondition. The player drags the nose onto a new aim
 * and LETS GO, and the velocity goes on coming round until it is there - at a
 * cruise speed the cap above binds for the whole close, so letting go is where
 * the correction starts rather than where it stops. `config.assistHeldOnly`
 * puts the gate back for the `--assist --inject assist-held-only` control.
 *
 * `state.assistCatchUp` is read only under that gate: armed by the caller on
 * the assist's OFF->ON edge, it is what closed an inherited gap with no axis
 * held back when nothing else did. It clears itself the moment the gap is gone
 * (the cap did not bind that call); this function only ever clears it.
 *
 * `assistDt` is ONE SUBSTEP's simulated seconds: `stepFlight` calls this once
 * per substep with that substep's own share, so the cap is
 * `accel * subDt / speed` with `subDt` never above `config.maxSubstepS` and no
 * frame ever rotates the velocity onto the aim in a single step. The caller
 * owns whether the assist acts at all (`src/main.ts`).
 */
function applyAssist(
  state: FlightState, axes: FlightAxes, config: FlightConfig, assistDt: number,
): void {
  const aim = state.assistAim;
  if (aim === null) return;
  const catchingUp = state.assistCatchUp;
  if (config.assistHeldOnly && !catchingUp && axes.pitch === 0 && axes.yaw === 0) return;
  const v = assistVelocity(state);
  const speed = Math.hypot(v.x, v.y, v.z);
  if (!(speed > 0)) return;
  const al = Math.hypot(aim.x, aim.y, aim.z);
  if (!(al > 0)) return;
  const ax = aim.x / al;
  const ay = aim.y / al;
  const az = aim.z / al;
  const cos = Math.min(1, Math.max(-1, (v.x * ax + v.y * ay + v.z * az) / speed));
  const ux = v.x / speed;
  const uy = v.y / speed;
  const uz = v.z / speed;
  // The aim off the velocity: the direction the thrusters push, and the sine
  // of the gap. Taken before the angle, because `acos` of a cosine that close
  // to 1 cannot read a closed gap below about 1e-8 rad and `atan2` off this
  // reads it to float precision. A closed gap is read once a substep for as
  // long as the assist is on, so what acos leaves behind is charged thousands
  // of times a warped frame.
  const rx = ax - cos * ux;
  const ry = ay - cos * uy;
  const rz = az - cos * uz;
  const rl = Math.hypot(rx, ry, rz);
  const gap = Math.atan2(rl, cos);
  const floor = config.assistNoFloor ? 0 : ASSIST_GAP_MIN_RAD;
  if (!(gap > floor)) { state.assistCatchUp = false; return; }
  // Retrograde, or near enough, leaves no plane to turn in; the next frame's
  // own turn (or the pull the ship is under) moves the nose off it. `rl` is the
  // sine of the gap, so this is the same floor read from the far end.
  if (!(rl > floor)) return;
  const step = config.assistFree ? gap : Math.min(gap, (config.accel * assistDt) / speed);
  if (step < gap) state.assistCapped = true;
  if (!(step > 0)) return;
  const c = Math.cos(step);
  const s = Math.sin(step);
  v.x = speed * (c * ux + (s * rx) / rl);
  v.y = speed * (c * uy + (s * ry) / rl);
  v.z = speed * (c * uz + (s * rz) / rl);
  const push = state.assistPush;
  push.x += (step * rx) / rl;
  push.y += (step * ry) / rl;
  push.z += (step * rz) / rl;
  if (!config.assistFree) state.deltaVSpent += speed * step;
  // The cap did not bind, so this call closed the whole gap: disarm.
  if (catchingUp && step >= gap) state.assistCatchUp = false;
}

/**
 * Carry the commanded aim through the turn a pull just put on the velocity.
 *
 * The aim is a direction the PLAYER named, and the gap the assist is billed
 * for is the one the player opened. Gravity turns the velocity of anything in
 * orbit at `g / speed` radians a second whether or not anybody is flying, and
 * an aim left fixed while that happens hands the assist a gap nobody commanded
 * every substep: `speed * (g / speed) * dt` is `g` metres a second of delta-v
 * for every simulated second, which under warp is hours of it inside a frame,
 * and it drags a parked orbit off its own track. Turning the aim by the same
 * arc leaves a commanded gap exactly as it was and holds an uncommanded one at
 * zero, which is what the null aim at boot already means: the ship's own
 * velocity, no gap and no spend.
 *
 * `before` is the velocity direction going into the kick, `v` the velocity it
 * left. Rodrigues about the shortest arc between the two.
 */
function carryAim(aim: FlightVec, before: FlightVec, v: FlightVec): void {
  const l = Math.hypot(v.x, v.y, v.z);
  if (!(l > 0)) return;
  const ux = v.x / l;
  const uy = v.y / l;
  const uz = v.z / l;
  const kx = before.y * uz - before.z * uy;
  const ky = before.z * ux - before.x * uz;
  const kz = before.x * uy - before.y * ux;
  const s = Math.hypot(kx, ky, kz);
  // A kick along the velocity changes the speed and not the direction.
  if (!(s > 0)) return;
  const angle = Math.atan2(s, before.x * ux + before.y * uy + before.z * uz);
  const nx = kx / s;
  const ny = ky / s;
  const nz = kz / s;
  const c = Math.cos(angle);
  const sn = Math.sin(angle);
  const d = (nx * aim.x + ny * aim.y + nz * aim.z) * (1 - c);
  const cx = ny * aim.z - nz * aim.y;
  const cy = nz * aim.x - nx * aim.z;
  const cz = nx * aim.y - ny * aim.x;
  aim.x = aim.x * c + cx * sn + nx * d;
  aim.y = aim.y * c + cy * sn + ny * d;
  aim.z = aim.z * c + cz * sn + nz * d;
}

/**
 * Stop the ship at the surface a substep's segment just met.
 *
 * Placed ON the radius the sweep met at the contact point - the swept root is a
 * float and HEIGHT is `range - that radius`, which must never paint a negative -
 * with the velocity set to the body's own, so the ship rides it from there. The
 * speed it arrived at is kept on the state and on the contact, since the notice
 * names it and a damage rule that does not exist yet would read it.
 *
 * The radius comes off the hit rather than off `radiiM`, so a body drawn as its
 * own shape seats on the drawn ground the sweep stopped against and not on the
 * datum sphere that ground runs kilometres either side of.
 */
function landOnGround(
  state: FlightState, pulls: readonly FlightPull[], ground: FlightGround,
  at: FlightVec, hit: GroundHit,
  sx: number, sy: number, sz: number, out: FlightVec,
  c0: readonly FlightVec[] | null, c1: readonly FlightVec[] | null, substepS: number,
): void {
  const i = hit.idx;
  const radiusM = hit.radiusM > 0 ? hit.radiusM : ground.radiiM[i] ?? 0;
  const cs = centreOf(pulls, c0, i);
  const ce = centreOf(pulls, c1, i);
  const f = hit.f;
  // The centre at the instant of the hit, so the offset is taken against where
  // the body was when the ship met it rather than where it ends the substep.
  const hx = cs.x + (ce.x - cs.x) * f;
  const hy = cs.y + (ce.y - cs.y) * f;
  const hz = cs.z + (ce.z - cs.z) * f;
  let ox = at.x + sx + (out.x - sx) * f - hx;
  let oy = at.y + sy + (out.y - sy) * f - hy;
  let oz = at.z + sz + (out.z - sz) * f - hz;
  const l = Math.hypot(ox, oy, oz);
  if (l > 0) {
    const k = radiusM / l;
    ox *= k;
    oy *= k;
    oz *= k;
  } else {
    ox = radiusM;
    oy = 0;
    oz = 0;
  }
  state.groundOffset.x = ox;
  state.groundOffset.y = oy;
  state.groundOffset.z = oz;
  // The offset is the ground under the ship at the hit; `out` is the substep's end.
  ground.spinBy?.(i, state.groundOffset, (1 - f) * substepS);
  const bv = ground.velIn === null ? null : ground.velIn[i] ?? null;
  const inv = c0 !== null && c1 !== null && substepS > 0 ? 1 / substepS : 0;
  const bx = inv !== 0 ? (ce.x - cs.x) * inv : bv?.x ?? 0;
  const by = inv !== 0 ? (ce.y - cs.y) * inv : bv?.y ?? 0;
  const bz = inv !== 0 ? (ce.z - cs.z) * inv : bv?.z ?? 0;
  const v = state.velocity;
  state.contactSpeedMps = Math.hypot(v.x - bx, v.y - by, v.z - bz);
  v.x = bx;
  v.y = by;
  v.z = bz;
  state.grounded = ground.ids[i] ?? '';
  ground.contact.hit = true;
  ground.contact.bodyId = state.grounded;
  ground.contact.speedMps = state.contactSpeedMps;
  out.x = ce.x + ox - at.x;
  out.y = ce.y + oy - at.y;
  out.z = ce.z + oz - at.z;
}

/**
 * Hold a resting ship where it is: the body's own centre at the end of this
 * substep, plus the offset the contact recorded turned through this substep's
 * spin. Re-read every substep, so the ship rides the body's travel and turn and
 * no substep of gravity gets to pull it under again. A set that no longer
 * carries the body leaves the ship still.
 */
function restOnGround(
  state: FlightState, pulls: readonly FlightPull[], ground: FlightGround | null,
  at: FlightVec, out: FlightVec,
  c0: readonly FlightVec[] | null, c1: readonly FlightVec[] | null, substepS: number,
): void {
  const v = state.velocity;
  const i = groundSlot(ground, state.grounded);
  const slot = i < 0 ? undefined : pulls[i];
  if (ground === null || slot === undefined) {
    // A body the ship is touching is the strongest pull there is, so this is
    // the set having dropped it entirely: hold the ship still rather than fall.
    v.x = 0;
    v.y = 0;
    v.z = 0;
    return;
  }
  const o = state.groundOffset;
  ground.spinBy?.(i, o, substepS);
  const cs = centreOf(pulls, c0, i);
  const ce = centreOf(pulls, c1, i);
  out.x = ce.x + o.x - at.x;
  out.y = ce.y + o.y - at.y;
  out.z = ce.z + o.z - at.z;
  const bv = ground.velIn === null ? null : ground.velIn[i] ?? null;
  const inv = c0 !== null && c1 !== null && substepS > 0 ? 1 / substepS : 0;
  v.x = inv !== 0 ? (ce.x - cs.x) * inv : bv?.x ?? 0;
  v.y = inv !== 0 ? (ce.y - cs.y) * inv : bv?.y ?? 0;
  v.z = inv !== 0 ? (ce.z - cs.z) * inv : bv?.z ?? 0;
}

/**
 * Whether the ship leaves the ground this substep: the NET acceleration on it,
 * burn less pull, taken against the outward normal.
 *
 * Not the burn's direction alone. The corvette's sustained thrust is under
 * 1 m/s2 and Mars's surface gravity is 3.7, so a nose-up burn that only has to
 * POINT out would lift the ship, have gravity drop it back, and post a contact
 * every frame. The ground pushing back exactly enough to cancel an inward
 * acceleration and no more is what a surface does, and it costs one dot
 * product: with no burn the net is the pull, which at a surface points in.
 */
function groundLifts(
  state: FlightState, pulls: readonly FlightPull[], config: FlightConfig,
  throttle: number, atX: number, atY: number, atZ: number,
  frameAccel: FlightVec | null | undefined,
): boolean {
  if (throttle === 0 || config.accel === 0) return false;
  const o = state.groundOffset;
  if (!(Math.hypot(o.x, o.y, o.z) > 0)) return false;
  _restAt.x = atX;
  _restAt.y = atY;
  _restAt.z = atZ;
  flightGravity(_restAt, pulls, _grav);
  const nose = flightNose(state, _nose);
  const dv = config.accel * throttle;
  const ax = _grav.x + nose.x * dv - (frameAccel?.x ?? 0);
  const ay = _grav.y + nose.y * dv - (frameAccel?.y ?? 0);
  const az = _grav.z + nose.z * dv - (frameAccel?.z ?? 0);
  return ax * o.x + ay * o.y + az * o.z > 0;
}

/**
 * One step of hand flight. `throttle` is [-1, 1] along the nose, `dt` is SIM
 * seconds for the translation, `attitudeDt` is REAL seconds for the rotation,
 * `field` is where the ship is and what pulls on it there (null for no
 * gravity at all).
 *
 * The player's hand runs in real seconds: a tap of a turn key or a drag turns
 * the ship by `config.turnRate` per real second whatever the sim clock or
 * `?flightwarp=` is doing, so `attitudeDt` is applied once, here, rather than
 * folded into the substep loop below - the substeps exist for the gravity
 * integrator's error, not for the hand, and a warped `dt` would otherwise warp
 * the turn too. Thrust, gravity and coasting stay on `dt` exactly as they were.
 *
 * Drift-kick-drift: half the position step, then the pull and the burn taken
 * at that midpoint, then the other half. Second order and time-reversible, so
 * a ship put on a circular orbit STAYS on one - the plainer kick-then-drift
 * form starts the orbit with half a kick of radial velocity it never asked
 * for, which over a 300 km Mars orbit reads as 1.4 km of eccentricity it takes
 * with it for good. A constant-throttle burn also covers exactly `a t^2 / 2`
 * this way instead of overshooting by half a step, and a coast with nothing
 * acting leaves the velocity bit-identical either way.
 *
 * Once something is pulling the step is split into substeps no longer than
 * `config.maxSubstepS`, each taking the pull at its own midpoint, so the
 * integrator's error follows that number and not the frame rate or the warp.
 *
 * `assistDt` is the SIMULATED seconds the flight assist may act over, 0 for
 * none, and is split across the substeps the same way `dt` is, so a turn under
 * warp costs what the same turn costs at real time; `applyAssist` above is
 * what it does and the caller owns the gate.
 *
 * `field.ground` is where the bodies' surfaces are. Each substep's own segment
 * is swept against every slot carrying a radius, and a segment that meets one
 * stops the ship on it: the remaining substeps then hold the ship against the
 * body rather than integrating it through the centre and out the far side.
 * The caller clears `field.ground.contact` before the frame and reads it after,
 * since one frame is flown as several calls; nothing here ever clears it.
 *
 * Returns the position delta for the whole step, in the caller's own frame.
 * Nothing but a burn, the assist, a pull and - on the `?drag=1` variant only -
 * the drag term ever writes the velocity. Gravity is never charged to
 * `deltaVSpent`.
 */
export function stepFlight<T extends FlightVec>(
  state: FlightState,
  axes: FlightAxes,
  throttle: number,
  config: FlightConfig,
  dt: number,
  attitudeDt: number,
  out: T,
  field: FlightField | null = null,
  assistDt = 0,
): T {
  out.x = 0;
  out.y = 0;
  out.z = 0;
  state.assistPush.x = 0;
  state.assistPush.y = 0;
  state.assistPush.z = 0;
  state.assistCapped = false;

  if (attitudeDt > 0) {
    const ax = axes.pitch * config.turnRate * attitudeDt;
    const ay = axes.yaw * config.turnRate * attitudeDt;
    const az = axes.roll * config.turnRate * attitudeDt;
    const angle = Math.hypot(ax, ay, az);
    if (angle > 0) rotateBody(state.orientation, ax / angle, ay / angle, az / angle, angle);
  }
  if (!(dt > 0)) return out;

  const pulls = field !== null ? field.pulls : EMPTY_PULLS;
  const n = flightSubsteps(config, dt, pulls.length, assistDt > 0);
  const h = dt / n;
  const assistH = assistDt > 0 ? assistDt / n : 0;
  const v = state.velocity;

  // A pitch or yaw is the player naming where the nose should be, and that
  // direction is what the assist flies the velocity onto from here - not
  // wherever the bow is pointing at the moment the switch is pressed.
  if (axes.pitch !== 0 || axes.yaw !== 0) aimAlongNose(state);

  const ground = field === null ? null : field.ground ?? null;
  // Every centre at this substep's own start and end, so a body that moves
  // under the segment is swept rather than stood still: null leaves every
  // sphere where `pulls` put it, which is all a caller with no grid can say.
  const centreAt = ground === null ? null : ground.centreAt;
  // The near end of substep i is the far end of substep i-1: read it once and
  // carry it forward rather than asking the grid for the same instant twice.
  let c0 = centreAt === null ? null : growCentres(_c0, pulls.length);
  let c1 = centreAt === null ? null : growCentres(_c1, pulls.length);
  const tau0 = ground === null ? 0 : ground.tau0;
  const centreParent = ground === null ? -1 : ground.centreParent;
  if (centreAt !== null) centreAt(tau0, c0!, pulls, centreParent);

  for (let i = 0; i < n; i++) {
    const half = h * 0.5;
    if (centreAt !== null) centreAt(tau0 + (i + 1) * h, c1!, pulls, centreParent);
    if (state.grounded !== null && field !== null) {
      // The slot this substep rests on, read before `restOnGround` may clear
      // `state.grounded` below: the orientation's own turn and a liftoff's
      // tangential velocity both read the same spin off it, once.
      const slot = ground === null ? -1 : groundSlot(ground, state.grounded);
      _spinOmega.x = 0; _spinOmega.y = 0; _spinOmega.z = 0;
      if (ground !== null && slot >= 0) ground.spinRateAt?.(slot, _spinOmega);
      restOnGround(state, pulls, ground, field.at, out, c0, c1, h);
      // While resting, the orientation turns by the same increment the offset
      // just did, so the landed view holds still against the ground instead of
      // yawing in the inertial frame; it stops the substep the ship lifts off.
      if (!config.inertialHeading) {
        const om = Math.hypot(_spinOmega.x, _spinOmega.y, _spinOmega.z);
        if (om > 0) {
          rotateWorld(state.orientation,
            _spinOmega.x / om, _spinOmega.y / om, _spinOmega.z / om, om * h);
        }
      }
      if (!groundLifts(state, pulls, config, throttle,
        field.at.x + out.x, field.at.y + out.y, field.at.z + out.z, field.frameAccel)) {
        if (centreAt !== null) { const swap = c0; c0 = c1; c1 = swap; }
        continue;
      }
      // Liftoff: `restOnGround` set `v` to the body's own centre velocity,
      // dropping the tangential speed the spinning offset carried. Add it
      // back from the same spin the offset was just turned by.
      if (!config.dropLiftoffSpin) {
        const o = state.groundOffset;
        v.x += _spinOmega.y * o.z - _spinOmega.z * o.y;
        v.y += _spinOmega.z * o.x - _spinOmega.x * o.z;
        v.z += _spinOmega.x * o.y - _spinOmega.y * o.x;
      }
      state.grounded = null;
      state.contactSpeedMps = 0;
    }
    // The substep's own start, so the segment swept against the ground below is
    // the one this substep actually flew.
    const sx = out.x;
    const sy = out.y;
    const sz = out.z;
    out.x += v.x * half;
    out.y += v.y * half;
    out.z += v.z * half;

    if (field !== null && pulls.length > 0) {
      _at.x = field.at.x + out.x;
      _at.y = field.at.y + out.y;
      _at.z = field.at.z + out.z;
      flightGravity(_at, pulls, _grav);
      const frameA = field.frameAccel;
      if (frameA) {
        _grav.x -= frameA.x;
        _grav.y -= frameA.y;
        _grav.z -= frameA.z;
      }
      const aim = config.assistFrozenAim ? null : state.assistAim;
      const s0 = aim === null ? 0 : Math.hypot(v.x, v.y, v.z);
      if (s0 > 0) {
        _vHat.x = v.x / s0;
        _vHat.y = v.y / s0;
        _vHat.z = v.z / s0;
      }
      v.x += _grav.x * h;
      v.y += _grav.y * h;
      v.z += _grav.z * h;
      if (aim !== null && s0 > 0) carryAim(aim, _vHat, v);
    }

    if (throttle !== 0 && config.accel !== 0) {
      const dv = config.accel * throttle * h;
      const nose = flightNose(state, _nose);
      v.x += nose.x * dv;
      v.y += nose.y * dv;
      v.z += nose.z * dv;
      state.deltaVSpent += Math.abs(dv);
    }
    if (assistH > 0) applyAssist(state, axes, config, assistH);
    if (config.drag > 0) {
      const k = Math.exp(-config.drag * h);
      v.x *= k;
      v.y *= k;
      v.z *= k;
    }

    out.x += v.x * half;
    out.y += v.y * half;
    out.z += v.z * half;

    if (ground !== null && field !== null
      && findGroundContact(pulls, ground,
        field.at.x + sx, field.at.y + sy, field.at.z + sz,
        field.at.x + out.x, field.at.y + out.y, field.at.z + out.z, c0, c1,
        tau0 + i * h, tau0 + (i + 1) * h, _hit)) {
      landOnGround(state, pulls, ground, field.at, _hit, sx, sy, sz, out, c0, c1, h);
      ground.contact.atS = tau0 + (i + _hit.f) * h;
    }
    if (centreAt !== null) { const swap = c0; c0 = c1; c1 = swap; }
  }
  return out;
}

/**
 * Move a resting ship to a different radius along the offset it already rests
 * at, metres, and say whether it moved.
 *
 * The sweep meets a SPHERE at the registry's mean `radius_m`, which is the one
 * surface a pure model of a body has. The globe cruise draws carries the body's
 * own relief on top of that - on Mars it runs 7 km under the mean radius and
 * 20 km over it - so a ship parked at the mean hangs over the picture or sinks
 * into it. The caller reads the drawn radius under the ship off the scene and
 * seats it there.
 */
export function seatOnGround(state: FlightState, radiusM: number): boolean {
  if (state.grounded === null || !(radiusM > 0)) return false;
  const o = state.groundOffset;
  const l = Math.hypot(o.x, o.y, o.z);
  if (!(l > 0) || radiusM === l) return false;
  const k = radiusM / l;
  o.x *= k;
  o.y *= k;
  o.z *= k;
  return true;
}

const EMPTY_PULLS: readonly FlightPull[] = [];
const _nose: FlightVec = { x: 0, y: 0, z: 0 };
const _at: FlightVec = { x: 0, y: 0, z: 0 };
const _grav: FlightVec = { x: 0, y: 0, z: 0 };
const _vHat: FlightVec = { x: 0, y: 0, z: 0 };
const _hit: GroundHit = createGroundHit();
const _restAt: FlightVec = { x: 0, y: 0, z: 0 };
const _spinOmega: FlightVec = { x: 0, y: 0, z: 0 };
const _c0: FlightVec[] = [];
const _c1: FlightVec[] = [];

/** One scratch centre per slot, kept across calls rather than allocated a frame. */
function growCentres(into: FlightVec[], n: number): FlightVec[] {
  while (into.length < n) into.push({ x: 0, y: 0, z: 0 });
  return into;
}
